import React, { useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Modal, Spin, Table } from "antd";
import { BarChartOutlined, AppstoreOutlined } from "@ant-design/icons";
import ScoreLegend from "./ScoreLegend";
import SectionHeatmapTable from "./SectionHeatmapTable";
import SectionBarChart from "./SectionBarChart";
import { CYCLE_COLORS } from "../../../utils/Service/colors";
import CycleBar from "../../../components/common/CycleBar";
import CardHeader from "../../../components/common/CardHeader";

const VIEWS = {
  BAR: "bar",
  // TABLE: "table",
  HEATMAP: "heatmap",
};

const SectionSummary = ({
  cycleIds,
  cycles,
  ssLoading,
  SectionSummaryData,
  downloading,
  downloadBarChartAsPNG,
}) => {
  const [activeView, setActiveView] = useState(VIEWS.HEATMAP);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const barChartRef = useRef(null);
  const sectionSummaryRef = useRef(null);

  const allSections = useMemo(() => {
    return (
      SectionSummaryData?.sections?.map((section) => section.section_name) || []
    );
  }, [SectionSummaryData]);

  const sectionDataByCycle = useMemo(() => {
    const result = {};

    SectionSummaryData?.sections?.forEach((section) => {
      section.cycles?.forEach((cycle) => {
        if (!result[cycle.audit_cycle_id]) {
          result[cycle.audit_cycle_id] = {};
        }

        result[cycle.audit_cycle_id][section.section_name] = cycle.value ?? 0;
      });
    });

    return result;
  }, [SectionSummaryData]);

  const cycleById = useMemo(() => {
    const map = {};

    cycles?.forEach((cycle) => {
      map[cycle.id] = cycle;
    });

    return map;
  }, [cycles]);

  const cards = useMemo(() => {
    return (
      cycleIds
        ?.map((id, index) => {
          const cycle = cycleById[id];

          if (!cycle) return null;

          return {
            ...cycle,
            id: cycle.id,
            name: cycle.name,
            date: cycle.date,
            color: CYCLE_COLORS[index % CYCLE_COLORS.length].main,
            scores: sectionDataByCycle[id] || {},
          };
        })
        .filter(Boolean) || []
    );
  }, [cycleIds, cycleById, sectionDataByCycle]);

  const showModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const modalColumns = [
    { title: "Section Name", dataIndex: "section", key: "section", width: 260 },
    ...cards.map((c) => ({
      title: c.name,
      dataIndex: `cycle_${c.id}`,
      key: `cycle_${c.id}`,
      align: "center",
      render: (v) => `${v}%`,
    })),
  ];

  const modalData = allSections.map((section, index) => {
    const row = { key: index, section };
    cards.forEach((c) => (row[`cycle_${c.id}`] = c.scores?.[section] ?? 0));
    return row;
  });

  const headerTitle = `Section Summary – Comparative View (
              ${
                activeView === VIEWS.HEATMAP
                  ? "Heatmap"
                  : activeView === VIEWS.TABLE
                    ? "Table"
                    : "Bar"
              })`;

  return (
    <>
      <Wrapper className="dashboard-card shadow p-3" ref={sectionSummaryRef}>
        <CardHeader
          title={headerTitle}
          subtitle="Compare section-wise performance up to 5 audit cycles"
          onEyeClick={showModal}
          downloading={downloading}
          onDownloadClick={() =>
            downloadBarChartAsPNG(sectionSummaryRef, "section-summary")
          }
        />
        <CycleBar cycleIds={cycleIds} cycles={cycles} />

        {/* Bars, Tables, HeatMap */}
        <PanelCard ref={barChartRef} className="upAuditsCard">
          <PanelHeaderRow>
            <div>
              {activeView === VIEWS.HEATMAP && <ScoreLegend />}
              <PanelSubtitle>
                Each {activeView === VIEWS.BAR ? "Bar" : "Cell"} shows the score
                (%) for a section in the selected cycles
              </PanelSubtitle>
            </div>
            <ViewToggle>
              <ToggleBtn
                active={activeView === VIEWS.BAR}
                onClick={() => setActiveView(VIEWS.BAR)}
              >
                <BarChartOutlined /> Bar View
              </ToggleBtn>
              <ToggleBtn
                active={activeView === VIEWS.HEATMAP}
                onClick={() => setActiveView(VIEWS.HEATMAP)}
              >
                <AppstoreOutlined /> Heatmap View
              </ToggleBtn>
            </ViewToggle>
          </PanelHeaderRow>

          <ViewBody>
            {cards.length === 0 ? (
              <EmptyState style={{ padding: "3rem 0" }}>
                Select at least one audit cycle to see data
              </EmptyState>
            ) : ssLoading ? (
              <Loader>
                <Spin />
              </Loader>
            ) : activeView === VIEWS.HEATMAP ? (
              <SectionHeatmapTable sections={allSections} cards={cards} />
            ) : (
              // ) : activeView === VIEWS.TABLE ? (
              //   <SectionTableView sections={allSections} cards={cards} />
              <SectionBarChart sections={allSections} cards={cards} />
            )}
          </ViewBody>
        </PanelCard>

        <FootNote>
          Scores are shown in percentage (%). Higher score indicates better
          performance.
        </FootNote>
      </Wrapper>

      {/* ======================= Modals =============================== */}
      <ModalStl
        title="All Section Data"
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Table
          dataSource={modalData}
          columns={modalColumns}
          rowKey="key"
          pagination={false}
          bordered={false}
        />
      </ModalStl>
    </>
  );
};

/* ---------------- styled ---------------- */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  font-family: "DM Sans", sans-serif;
`;

const PanelCard = styled.div`
  border: 1px solid #eaecef;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  background: #ffffff;
`;

const PanelHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PanelSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 0.8rem;
  color: #8a94a6;
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #dde1e6;
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  padding: 8px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props.active ? "#007dc1" : "#ffffff")};
  color: ${(props) => (props.active ? "#ffffff" : "#353e4c")};
  &:not(:last-of-type) {
    border-right: 1px solid #dde1e6;
  }
`;

const ViewBody = styled.div`
  margin-top: 0.75rem;
`;

const EmptyState = styled.div`
  color: #8a94a6;
  font-size: 0.85rem;
  text-align: center;
`;

const FootNote = styled.div`
  background: #f8f9fb;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.78rem;
  color: #8a94a6;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalStl = styled(Modal)`
  :where(.css-dev-only-do-not-override-1kf000u).ant-modal .ant-modal-title {
    color: #003c5d !important;
  }
`;

const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;
export default SectionSummary;
